import {Injectable, Inject, Pipe, PipeTransform} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, Observable, timer, of} from 'rxjs';
import {map, switchMap, share, catchError} from 'rxjs/operators';
import {mapParseItem} from './schema-validation';
import {FEATURE_FLAG_CONFIG, FeatureFlagConfig} from './feature-flag.config';
import {z} from 'zod';
import {filterNulls} from './rxjs';

export type FeatureFlag = string | {id: string; hideWhenEnabled?: boolean};

@Injectable({providedIn: 'root'})
export class FeatureFlagService {
    private readonly cacheTimeout: number;
    private flagsSubject = new BehaviorSubject<Record<string, boolean> | null>(null);
    private readonly apiUrl: string;
    private readonly allEnabled: boolean;

    constructor(
        private http: HttpClient,
        @Inject(FEATURE_FLAG_CONFIG) config: FeatureFlagConfig,
    ) {
        this.apiUrl = config.url;
        this.cacheTimeout = config.cacheTimeout ?? 30_000; // 30 seconds default
        this.allEnabled = config.allEnabled ?? false;

        if (this.allEnabled) {
            return;
        }

        const flags$ = timer(0, this.cacheTimeout).pipe(
            switchMap(() =>
                this.http.get(this.apiUrl).pipe(
                    mapParseItem(z.record(z.string(), z.boolean())),
                    catchError(() => of<Record<string, boolean>>({})),
                ),
            ),
            share(),
        );

        // Subscribe to flags updates
        flags$.subscribe(flags => this.flagsSubject.next(flags));
    }

    isFeatureEnabled(featureFlagId: string): boolean {
        if (this.allEnabled) {
            return true;
        }
        return this.flagsSubject.value?.[featureFlagId] ?? false;
    }

    isFeatureEnabled$(featureFlagId: string): Observable<boolean> {
        if (this.allEnabled) {
            return of(true);
        }
        return this.flagsSubject.pipe(
            filterNulls(),
            map(flags => flags[featureFlagId] ?? false),
        );
    }

    isFeatureFlagEnabled$(featureFlag: FeatureFlag | undefined): Observable<boolean> {
        if (featureFlag === undefined) {
            return of(true);
        }

        if (typeof featureFlag === 'string') {
            return this.isFeatureEnabled$(featureFlag);
        }

        return this.isFeatureEnabled$(featureFlag.id).pipe(
            map(isEnabled => (featureFlag.hideWhenEnabled === true ? !isEnabled : isEnabled)),
        );
    }
}

@Pipe({
    name: 'isFeatureFlagEnabled',
})
export class IsFeatureFlagEnabledPipe implements PipeTransform {
    constructor(private featureFlagService: FeatureFlagService) {}

    transform(featureFlag: FeatureFlag | undefined): Observable<boolean> {
        return this.featureFlagService.isFeatureFlagEnabled$(featureFlag);
    }
}
